import React from 'react';
import Icon from '../../../components/AppIcon';

const QuickHelpTopics = ({ userRole, currentScreen, onTopicClick }) => {
  const getTopicsForRole = () => {
    const roleTopics = {
      member: [
        { icon: 'Calendar', label: 'How to book a class', query: 'How do I book a class?' },
        { icon: 'Dumbbell', label: 'View my workout programs', query: 'How do I view my workout programs?' },
        { icon: 'User', label: 'Update my profile', query: 'How do I update my profile information?' },
        { icon: 'CreditCard', label: 'Manage my membership', query: 'How do I manage my membership and payments?' },
        { icon: 'MessageSquare', label: 'Contact my trainer', query: 'How do I message my trainer?' },
        { icon: 'TrendingUp', label: 'Track my progress', query: 'How do I track my fitness progress?' }
      ],
      staff: [
        { icon: 'UserCheck', label: 'Check in members', query: 'How do I check in members?' },
        { icon: 'ShoppingCart', label: 'Process POS sales', query: 'How do I process a POS transaction?' },
        { icon: 'Calendar', label: 'Manage class schedules', query: 'How do I manage class schedules?' },
        { icon: 'Users', label: 'Look up member info', query: 'How do I look up member information?' },
        { icon: 'MessageSquare', label: 'Send messages', query: 'How do I send messages to members?' },
        { icon: 'Package', label: 'Check inventory', query: 'How do I check inventory levels?' }
      ],
      trainer: [
        { icon: 'Users', label: 'Manage my clients', query: 'How do I manage my client list?' },
        { icon: 'Dumbbell', label: 'Create workout programs', query: 'How do I create workout programs?' },
        { icon: 'TrendingUp', label: 'Track client progress', query: 'How do I track client progress?' },
        { icon: 'Calendar', label: 'Schedule sessions', query: 'How do I schedule training sessions?' },
        { icon: 'DollarSign', label: 'View my commissions', query: 'How do I view my commission earnings?' },
        { icon: 'MessageSquare', label: 'Message clients', query: 'How do I message my clients?' }
      ],
      manager: [
        { icon: 'BarChart3', label: 'View analytics', query: 'How do I view gym analytics and reports?' },
        { icon: 'Users', label: 'Manage staff', query: 'How do I manage staff members?' },
        { icon: 'Package', label: 'Manage inventory', query: 'How do I manage inventory across locations?' },
        { icon: 'DollarSign', label: 'Financial reports', query: 'How do I access financial reports?' },
        { icon: 'UserPlus', label: 'Register new members', query: 'How do I register new members?' },
        { icon: 'Settings', label: 'Configure settings', query: 'How do I configure gym settings?' }
      ],
      owner: [
        { icon: 'DollarSign', label: 'Financial reconciliation', query: 'How do I access financial reconciliation?' },
        { icon: 'BarChart3', label: 'Performance analytics', query: 'How do I compare gym performance?' },
        { icon: 'Users', label: 'Staff performance', query: 'How do I view staff performance analytics?' },
        { icon: 'Package', label: 'Location-based inventory', query: 'How do I manage inventory by location?' },
        { icon: 'Settings', label: 'System configuration', query: 'How do I configure system-wide settings?' }
      ],
      sysadmin: [
        { icon: 'Shield', label: 'System health', query: 'How do I monitor system health?' },
        { icon: 'Building2', label: 'Manage all gyms', query: 'How do I manage all gym locations?' },
        { icon: 'Users', label: 'Global user management', query: 'How do I manage users across all gyms?' },
        { icon: 'DollarSign', label: 'SaaS revenue', query: 'How do I view SaaS subscription revenue?' },
        { icon: 'Database', label: 'Database management', query: 'How do I manage database and migrations?' },
        { icon: 'Settings', label: 'Platform configuration', query: 'How do I configure platform settings?' }
      ]
    };

    return roleTopics?.[userRole] || roleTopics?.['member'];
  };

  const getScreenSpecificTopics = () => {
    const screenTopics = {
      'pos-inventory-management': [
        { icon: 'ShoppingCart', label: 'Process a sale', query: 'How do I process a POS sale?' },
        { icon: 'Package', label: 'Add new product', query: 'How do I add a new product to inventory?' },
        { icon: 'Barcode', label: 'Use barcode scanner', query: 'How do I use the barcode scanner?' },
        { icon: 'CreditCard', label: 'Payment methods', query: 'What payment methods are available?' }
      ],
      'location-based-pos-inventory': [
        { icon: 'MapPin', label: 'Switch locations', query: 'How do I switch between gym locations?' },
        { icon: 'ArrowLeftRight', label: 'Transfer inventory', query: 'How do I transfer inventory between locations?' },
        { icon: 'BarChart3', label: 'Location analytics', query: 'How do I view analytics for a specific location?' },
        { icon: 'AlertTriangle', label: 'Low stock alerts', query: 'How do low stock alerts work?' }
      ],
      'member-management': [
        { icon: 'UserPlus', label: 'Add new member', query: 'How do I add a new member?' },
        { icon: 'Search', label: 'Search members', query: 'How do I search for members?' },
        { icon: 'Edit', label: 'Edit member info', query: 'How do I edit member information?' },
        { icon: 'Ban', label: 'Suspend membership', query: 'How do I suspend a membership?' }
      ],
      'analytics-dashboard': [
        { icon: 'BarChart3', label: 'Understand metrics', query: 'What do the analytics metrics mean?' },
        { icon: 'Calendar', label: 'Change date range', query: 'How do I change the date range for analytics?' },
        { icon: 'Download', label: 'Export reports', query: 'How do I export analytics reports?' },
        { icon: 'Sparkles', label: 'AI insights', query: 'How do I use AI-powered insights?' }
      ]
    };

    return screenTopics?.[currentScreen] || [];
  };

  const roleTopics = getTopicsForRole();
  const screenTopics = getScreenSpecificTopics();

  return (
    <div className="flex flex-col h-full overflow-y-auto p-4 space-y-6">
      {/* Screen-Specific Topics */}
      {screenTopics?.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
            <Icon name="Zap" size={16} color="var(--color-accent)" />
            Quick Help for This Page
          </h3>
          <div className="grid grid-cols-1 gap-2">
            {screenTopics?.map((topic, index) => (
              <button
                key={index}
                onClick={() => onTopicClick(topic?.query)}
                className="text-left px-4 py-3 bg-accent/5 hover:bg-accent/10 border border-accent/20 rounded-lg transition-colors flex items-center gap-3"
              >
                <div className="w-8 h-8 rounded-lg bg-accent/10 flex items-center justify-center flex-shrink-0">
                  <Icon name={topic?.icon} size={16} color="var(--color-accent)" />
                </div>
                <span className="text-sm font-medium text-foreground">{topic?.label}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Role-Based Topics */}
      <div>
        <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
          <Icon name="User" size={16} color="var(--color-primary)" />
          Common Tasks for {userRole?.charAt(0)?.toUpperCase() + userRole?.slice(1)}s
        </h3>
        <div className="grid grid-cols-1 gap-2">
          {roleTopics?.map((topic, index) => (
            <button
              key={index}
              onClick={() => onTopicClick(topic?.query)}
              className="text-left px-4 py-3 bg-muted hover:bg-muted/80 rounded-lg transition-colors flex items-center gap-3"
            >
              <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Icon name={topic?.icon} size={16} color="var(--color-primary)" />
              </div>
              <span className="text-sm font-medium text-foreground">{topic?.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* General Help */}
      <div>
        <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
          <Icon name="HelpCircle" size={16} color="var(--color-muted-foreground)" />
          General Help
        </h3>
        <div className="grid grid-cols-1 gap-2">
          <button
            onClick={() => onTopicClick('What features are available to me?')}
            className="text-left px-4 py-3 bg-muted hover:bg-muted/80 rounded-lg transition-colors flex items-center gap-3"
          >
            <Icon name="List" size={16} color="var(--color-muted-foreground)" />
            <span className="text-sm font-medium text-foreground">What features are available?</span>
          </button>
          <button
            onClick={() => onTopicClick('How do I navigate the platform?')}
            className="text-left px-4 py-3 bg-muted hover:bg-muted/80 rounded-lg transition-colors flex items-center gap-3"
          >
            <Icon name="Navigation" size={16} color="var(--color-muted-foreground)" />
            <span className="text-sm font-medium text-foreground">Platform navigation</span>
          </button>
          <button
            onClick={() => onTopicClick('How do I get technical support?')}
            className="text-left px-4 py-3 bg-muted hover:bg-muted/80 rounded-lg transition-colors flex items-center gap-3"
          >
            <Icon name="LifeBuoy" size={16} color="var(--color-muted-foreground)" />
            <span className="text-sm font-medium text-foreground">Technical support</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default QuickHelpTopics;